/**
 * AminoBae — Workout Tracker data layer
 * Per-device localStorage. If a Shopify customer is logged in, the storage
 * key is namespaced by their customer ID so multiple accounts on the same
 * device stay separate, and a user's data follows their login on this device.
 * Cross-device sync requires a backend (not implemented in pure theme).
 *
 * Set window.WorkoutDataNamespace BEFORE this script loads to opt into per-user
 * isolation (the tracker section sets it from {{ customer.id }}).
 *
 * Exposes window.WorkoutData for the tracker + analytics sections.
 */
(function() {
  const ns = String(window.WorkoutDataNamespace || '').replace(/[^a-zA-Z0-9_-]/g, '');
  const STORAGE_KEY = ns ? ('aminobae_workout_v1__' + ns) : 'aminobae_workout_v1';

  // ── Curated exercise database ─────────────────────────────────────
  const EXERCISES = [
    // ── CHEST ──────────────────────────────────────────────────────
    { name: 'Barbell Bench Press',          muscle: 'Chest',     equipment: 'Barbell'    },
    { name: 'Incline Barbell Press',        muscle: 'Chest',     equipment: 'Barbell'    },
    { name: 'Decline Barbell Press',        muscle: 'Chest',     equipment: 'Barbell'    },
    { name: 'Dumbbell Bench Press',         muscle: 'Chest',     equipment: 'Dumbbell'   },
    { name: 'Incline Dumbbell Press',       muscle: 'Chest',     equipment: 'Dumbbell'   },
    { name: 'Decline Dumbbell Press',       muscle: 'Chest',     equipment: 'Dumbbell'   },
    { name: 'Cable Fly (Mid)',              muscle: 'Chest',     equipment: 'Cable'      },
    { name: 'Cable Fly (High to Low)',      muscle: 'Chest',     equipment: 'Cable'      },
    { name: 'Cable Fly (Low to High)',      muscle: 'Chest',     equipment: 'Cable'      },
    { name: 'Dumbbell Fly',                 muscle: 'Chest',     equipment: 'Dumbbell'   },
    { name: 'Incline Dumbbell Fly',         muscle: 'Chest',     equipment: 'Dumbbell'   },
    { name: 'Push-up',                      muscle: 'Chest',     equipment: 'Bodyweight' },
    { name: 'Wide-Grip Push-up',            muscle: 'Chest',     equipment: 'Bodyweight' },
    { name: 'Diamond Push-up',              muscle: 'Chest',     equipment: 'Bodyweight' },
    { name: 'Dip',                          muscle: 'Chest',     equipment: 'Bodyweight' },
    { name: 'Chest Press Machine',          muscle: 'Chest',     equipment: 'Machine'    },
    { name: 'Pec Deck Fly',                 muscle: 'Chest',     equipment: 'Machine'    },
    { name: 'Landmine Press',               muscle: 'Chest',     equipment: 'Barbell'    },
    { name: 'Svend Press',                  muscle: 'Chest',     equipment: 'Other'      },

    // ── BACK ────────────────────────────────────────────────────────
    { name: 'Deadlift',                     muscle: 'Back',      equipment: 'Barbell'    },
    { name: 'Romanian Deadlift',            muscle: 'Back',      equipment: 'Barbell'    },
    { name: 'Stiff-Leg Deadlift',           muscle: 'Back',      equipment: 'Barbell'    },
    { name: 'Sumo Deadlift',                muscle: 'Back',      equipment: 'Barbell'    },
    { name: 'Pull-up',                      muscle: 'Back',      equipment: 'Bodyweight' },
    { name: 'Chin-up',                      muscle: 'Back',      equipment: 'Bodyweight' },
    { name: 'Wide-Grip Pull-up',            muscle: 'Back',      equipment: 'Bodyweight' },
    { name: 'Neutral-Grip Pull-up',         muscle: 'Back',      equipment: 'Bodyweight' },
    { name: 'Lat Pulldown',                 muscle: 'Back',      equipment: 'Cable'      },
    { name: 'Wide-Grip Lat Pulldown',       muscle: 'Back',      equipment: 'Cable'      },
    { name: 'Neutral-Grip Lat Pulldown',    muscle: 'Back',      equipment: 'Cable'      },
    { name: 'Straight-Arm Pulldown',        muscle: 'Back',      equipment: 'Cable'      },
    { name: 'Bent-over Row',                muscle: 'Back',      equipment: 'Barbell'    },
    { name: 'Pendlay Row',                  muscle: 'Back',      equipment: 'Barbell'    },
    { name: 'Dumbbell Row',                 muscle: 'Back',      equipment: 'Dumbbell'   },
    { name: 'Chest-Supported Row',          muscle: 'Back',      equipment: 'Dumbbell'   },
    { name: 'Cable Seated Row',             muscle: 'Back',      equipment: 'Cable'      },
    { name: 'Cable Wide-Row',               muscle: 'Back',      equipment: 'Cable'      },
    { name: 'T-Bar Row',                    muscle: 'Back',      equipment: 'Barbell'    },
    { name: 'Face Pull',                    muscle: 'Back',      equipment: 'Cable'      },
    { name: 'Meadows Row',                  muscle: 'Back',      equipment: 'Barbell'    },
    { name: 'Inverted Row',                 muscle: 'Back',      equipment: 'Bodyweight' },
    { name: 'Machine Row',                  muscle: 'Back',      equipment: 'Machine'    },
    { name: 'Good Morning',                 muscle: 'Back',      equipment: 'Barbell'    },
    { name: 'Back Extension',               muscle: 'Back',      equipment: 'Machine'    },
    { name: 'Hyperextension',               muscle: 'Back',      equipment: 'Bodyweight' },

    // ── LEGS ────────────────────────────────────────────────────────
    { name: 'Back Squat',                   muscle: 'Legs',      equipment: 'Barbell'    },
    { name: 'Front Squat',                  muscle: 'Legs',      equipment: 'Barbell'    },
    { name: 'Pause Squat',                  muscle: 'Legs',      equipment: 'Barbell'    },
    { name: 'Goblet Squat',                 muscle: 'Legs',      equipment: 'Dumbbell'   },
    { name: 'Hack Squat',                   muscle: 'Legs',      equipment: 'Machine'    },
    { name: 'Zercher Squat',                muscle: 'Legs',      equipment: 'Barbell'    },
    { name: 'Bulgarian Split Squat',        muscle: 'Legs',      equipment: 'Dumbbell'   },
    { name: 'Walking Lunge',                muscle: 'Legs',      equipment: 'Dumbbell'   },
    { name: 'Reverse Lunge',                muscle: 'Legs',      equipment: 'Dumbbell'   },
    { name: 'Lateral Lunge',                muscle: 'Legs',      equipment: 'Dumbbell'   },
    { name: 'Step-up',                      muscle: 'Legs',      equipment: 'Dumbbell'   },
    { name: 'Leg Press',                    muscle: 'Legs',      equipment: 'Machine'    },
    { name: 'Wide-Stance Leg Press',        muscle: 'Legs',      equipment: 'Machine'    },
    { name: 'Leg Extension',                muscle: 'Legs',      equipment: 'Machine'    },
    { name: 'Leg Curl (Lying)',             muscle: 'Legs',      equipment: 'Machine'    },
    { name: 'Leg Curl (Seated)',            muscle: 'Legs',      equipment: 'Machine'    },
    { name: 'Nordic Curl',                  muscle: 'Legs',      equipment: 'Bodyweight' },
    { name: 'Hip Thrust',                   muscle: 'Legs',      equipment: 'Barbell'    },
    { name: 'Dumbbell Hip Thrust',          muscle: 'Legs',      equipment: 'Dumbbell'   },
    { name: 'Glute Bridge',                 muscle: 'Legs',      equipment: 'Bodyweight' },
    { name: 'Cable Kickback',               muscle: 'Legs',      equipment: 'Cable'      },
    { name: 'Abductor Machine',             muscle: 'Legs',      equipment: 'Machine'    },
    { name: 'Adductor Machine',             muscle: 'Legs',      equipment: 'Machine'    },
    { name: 'Calf Raise (Standing)',        muscle: 'Legs',      equipment: 'Machine'    },
    { name: 'Calf Raise (Seated)',          muscle: 'Legs',      equipment: 'Machine'    },
    { name: 'Donkey Calf Raise',            muscle: 'Legs',      equipment: 'Machine'    },
    { name: 'Box Jump',                     muscle: 'Legs',      equipment: 'Bodyweight' },
    { name: 'Pistol Squat',                 muscle: 'Legs',      equipment: 'Bodyweight' },
    { name: 'Wall Sit',                     muscle: 'Legs',      equipment: 'Bodyweight' },

    // ── SHOULDERS ───────────────────────────────────────────────────
    { name: 'Overhead Press',               muscle: 'Shoulders', equipment: 'Barbell'    },
    { name: 'Push Press',                   muscle: 'Shoulders', equipment: 'Barbell'    },
    { name: 'Seated Barbell Press',         muscle: 'Shoulders', equipment: 'Barbell'    },
    { name: 'Seated Dumbbell Press',        muscle: 'Shoulders', equipment: 'Dumbbell'   },
    { name: 'Arnold Press',                 muscle: 'Shoulders', equipment: 'Dumbbell'   },
    { name: 'Z-Press',                      muscle: 'Shoulders', equipment: 'Barbell'    },
    { name: 'Lateral Raise',                muscle: 'Shoulders', equipment: 'Dumbbell'   },
    { name: 'Cable Lateral Raise',          muscle: 'Shoulders', equipment: 'Cable'      },
    { name: 'Machine Lateral Raise',        muscle: 'Shoulders', equipment: 'Machine'    },
    { name: 'Front Raise',                  muscle: 'Shoulders', equipment: 'Dumbbell'   },
    { name: 'Cable Front Raise',            muscle: 'Shoulders', equipment: 'Cable'      },
    { name: 'Plate Front Raise',            muscle: 'Shoulders', equipment: 'Other'      },
    { name: 'Rear Delt Fly',                muscle: 'Shoulders', equipment: 'Dumbbell'   },
    { name: 'Cable Rear Delt Fly',          muscle: 'Shoulders', equipment: 'Cable'      },
    { name: 'Reverse Pec Deck',             muscle: 'Shoulders', equipment: 'Machine'    },
    { name: 'Upright Row',                  muscle: 'Shoulders', equipment: 'Barbell'    },
    { name: 'Cable Upright Row',            muscle: 'Shoulders', equipment: 'Cable'      },
    { name: 'Dumbbell Shrug',               muscle: 'Shoulders', equipment: 'Dumbbell'   },
    { name: 'Barbell Shrug',                muscle: 'Shoulders', equipment: 'Barbell'    },
    { name: 'Band Pull-Apart',              muscle: 'Shoulders', equipment: 'Other'      },

    // ── ARMS ────────────────────────────────────────────────────────
    { name: 'Barbell Curl',                 muscle: 'Arms',      equipment: 'Barbell'    },
    { name: 'EZ-Bar Curl',                  muscle: 'Arms',      equipment: 'Barbell'    },
    { name: 'Dumbbell Curl',                muscle: 'Arms',      equipment: 'Dumbbell'   },
    { name: 'Hammer Curl',                  muscle: 'Arms',      equipment: 'Dumbbell'   },
    { name: 'Incline Dumbbell Curl',        muscle: 'Arms',      equipment: 'Dumbbell'   },
    { name: 'Concentration Curl',           muscle: 'Arms',      equipment: 'Dumbbell'   },
    { name: 'Preacher Curl',                muscle: 'Arms',      equipment: 'Barbell'    },
    { name: 'Machine Preacher Curl',        muscle: 'Arms',      equipment: 'Machine'    },
    { name: 'Cable Curl',                   muscle: 'Arms',      equipment: 'Cable'      },
    { name: 'Cable Hammer Curl',            muscle: 'Arms',      equipment: 'Cable'      },
    { name: 'Spider Curl',                  muscle: 'Arms',      equipment: 'Dumbbell'   },
    { name: 'Reverse Curl',                 muscle: 'Arms',      equipment: 'Barbell'    },
    { name: 'Wrist Curl',                   muscle: 'Arms',      equipment: 'Barbell'    },
    { name: 'Triceps Pushdown',             muscle: 'Arms',      equipment: 'Cable'      },
    { name: 'Triceps Rope Pushdown',        muscle: 'Arms',      equipment: 'Cable'      },
    { name: 'Skullcrusher',                 muscle: 'Arms',      equipment: 'Barbell'    },
    { name: 'Dumbbell Skullcrusher',        muscle: 'Arms',      equipment: 'Dumbbell'   },
    { name: 'Overhead Triceps Extension',   muscle: 'Arms',      equipment: 'Dumbbell'   },
    { name: 'Cable Overhead Extension',     muscle: 'Arms',      equipment: 'Cable'      },
    { name: 'Close-Grip Bench Press',       muscle: 'Arms',      equipment: 'Barbell'    },
    { name: 'Triceps Kickback',             muscle: 'Arms',      equipment: 'Dumbbell'   },
    { name: 'JM Press',                     muscle: 'Arms',      equipment: 'Barbell'    },
    { name: 'Tate Press',                   muscle: 'Arms',      equipment: 'Dumbbell'   },

    // ── CORE ────────────────────────────────────────────────────────
    { name: 'Plank',                        muscle: 'Core',      equipment: 'Bodyweight' },
    { name: 'Side Plank',                   muscle: 'Core',      equipment: 'Bodyweight' },
    { name: 'RKC Plank',                    muscle: 'Core',      equipment: 'Bodyweight' },
    { name: 'Hanging Leg Raise',            muscle: 'Core',      equipment: 'Bodyweight' },
    { name: 'Hanging Knee Raise',           muscle: 'Core',      equipment: 'Bodyweight' },
    { name: 'Captain\'s Chair Raise',       muscle: 'Core',      equipment: 'Machine'    },
    { name: 'Cable Crunch',                 muscle: 'Core',      equipment: 'Cable'      },
    { name: 'Machine Crunch',               muscle: 'Core',      equipment: 'Machine'    },
    { name: 'Decline Sit-up',               muscle: 'Core',      equipment: 'Bodyweight' },
    { name: 'Russian Twist',                muscle: 'Core',      equipment: 'Bodyweight' },
    { name: 'Ab Wheel Rollout',             muscle: 'Core',      equipment: 'Bodyweight' },
    { name: 'Pallof Press',                 muscle: 'Core',      equipment: 'Cable'      },
    { name: 'Dead Bug',                     muscle: 'Core',      equipment: 'Bodyweight' },
    { name: 'Bird Dog',                     muscle: 'Core',      equipment: 'Bodyweight' },
    { name: 'L-Sit Hold',                   muscle: 'Core',      equipment: 'Bodyweight' },
    { name: 'Hollow Body Hold',             muscle: 'Core',      equipment: 'Bodyweight' },
    { name: 'Landmine Rotation',            muscle: 'Core',      equipment: 'Barbell'    },
    { name: 'Woodchop (High to Low)',       muscle: 'Core',      equipment: 'Cable'      },
    { name: 'Woodchop (Low to High)',       muscle: 'Core',      equipment: 'Cable'      },
    { name: 'Mountain Climber',             muscle: 'Core',      equipment: 'Bodyweight' },
    { name: 'Toe Touch',                    muscle: 'Core',      equipment: 'Bodyweight' },

    // ── OLYMPIC / POWER ─────────────────────────────────────────────
    { name: 'Power Clean',                  muscle: 'Olympic',   equipment: 'Barbell'    },
    { name: 'Hang Clean',                   muscle: 'Olympic',   equipment: 'Barbell'    },
    { name: 'Power Snatch',                 muscle: 'Olympic',   equipment: 'Barbell'    },
    { name: 'Snatch',                       muscle: 'Olympic',   equipment: 'Barbell'    },
    { name: 'Clean & Jerk',                 muscle: 'Olympic',   equipment: 'Barbell'    },
    { name: 'Hang Power Clean',             muscle: 'Olympic',   equipment: 'Barbell'    },
    { name: 'High Pull',                    muscle: 'Olympic',   equipment: 'Barbell'    },
    { name: 'Push Press',                   muscle: 'Olympic',   equipment: 'Barbell'    },
    { name: 'Jerk',                         muscle: 'Olympic',   equipment: 'Barbell'    },
    { name: 'Trap Bar Deadlift',            muscle: 'Olympic',   equipment: 'Barbell'    },

    // ── CARDIO ──────────────────────────────────────────────────────
    { name: 'Treadmill Run',                muscle: 'Cardio',    equipment: 'Machine'    },
    { name: 'Treadmill Walk (Incline)',     muscle: 'Cardio',    equipment: 'Machine'    },
    { name: 'Stationary Bike',              muscle: 'Cardio',    equipment: 'Machine'    },
    { name: 'Rowing Machine',               muscle: 'Cardio',    equipment: 'Machine'    },
    { name: 'Stair Master',                 muscle: 'Cardio',    equipment: 'Machine'    },
    { name: 'Elliptical',                   muscle: 'Cardio',    equipment: 'Machine'    },
    { name: 'Jump Rope',                    muscle: 'Cardio',    equipment: 'Bodyweight' },
    { name: 'Battle Ropes',                 muscle: 'Cardio',    equipment: 'Other'      },
    { name: 'Sled Push',                    muscle: 'Cardio',    equipment: 'Machine'    },
    { name: 'Sled Pull',                    muscle: 'Cardio',    equipment: 'Machine'    },
    { name: 'Burpee',                       muscle: 'Cardio',    equipment: 'Bodyweight' },
    { name: 'Box Jump',                     muscle: 'Cardio',    equipment: 'Bodyweight' },
    { name: 'Assault Bike',                 muscle: 'Cardio',    equipment: 'Machine'    },
    { name: 'Ski Erg',                      muscle: 'Cardio',    equipment: 'Machine'    },

    // ── GLUTES ──────────────────────────────────────────────────────
    { name: 'Barbell Hip Thrust',           muscle: 'Glutes',    equipment: 'Barbell'    },
    { name: 'Dumbbell Hip Thrust',          muscle: 'Glutes',    equipment: 'Dumbbell'   },
    { name: 'Glute Bridge',                 muscle: 'Glutes',    equipment: 'Bodyweight' },
    { name: 'Single-Leg Hip Thrust',        muscle: 'Glutes',    equipment: 'Bodyweight' },
    { name: 'Cable Kickback',               muscle: 'Glutes',    equipment: 'Cable'      },
    { name: 'Donkey Kickback',              muscle: 'Glutes',    equipment: 'Bodyweight' },
    { name: 'Sumo Deadlift',                muscle: 'Glutes',    equipment: 'Barbell'    },
    { name: 'Romanian Deadlift',            muscle: 'Glutes',    equipment: 'Barbell'    },
    { name: 'Abductor Machine',             muscle: 'Glutes',    equipment: 'Machine'    },
    { name: 'Step-up with Kickback',        muscle: 'Glutes',    equipment: 'Dumbbell'   },
    { name: 'Frog Pump',                    muscle: 'Glutes',    equipment: 'Bodyweight' },
    { name: 'Lateral Band Walk',            muscle: 'Glutes',    equipment: 'Other'      },
    { name: 'Banded Squat',                 muscle: 'Glutes',    equipment: 'Other'      },
    { name: 'Squat to Kickback',            muscle: 'Glutes',    equipment: 'Bodyweight' },

    // ── HAMSTRINGS ──────────────────────────────────────────────────
    { name: 'Romanian Deadlift (DB)',       muscle: 'Hamstrings', equipment: 'Dumbbell'  },
    { name: 'Stiff-Leg Deadlift',           muscle: 'Hamstrings', equipment: 'Barbell'   },
    { name: 'Leg Curl (Lying)',             muscle: 'Hamstrings', equipment: 'Machine'   },
    { name: 'Leg Curl (Seated)',            muscle: 'Hamstrings', equipment: 'Machine'   },
    { name: 'Nordic Hamstring Curl',        muscle: 'Hamstrings', equipment: 'Bodyweight'},
    { name: 'Glute-Ham Raise',              muscle: 'Hamstrings', equipment: 'Machine'   },
    { name: 'Good Morning',                 muscle: 'Hamstrings', equipment: 'Barbell'   },
    { name: 'Hip Hinge (Band)',             muscle: 'Hamstrings', equipment: 'Other'     },
    { name: 'Single-Leg RDL',              muscle: 'Hamstrings', equipment: 'Dumbbell'  },

    // ── CALVES ──────────────────────────────────────────────────────
    { name: 'Standing Calf Raise',         muscle: 'Calves',    equipment: 'Machine'    },
    { name: 'Seated Calf Raise',           muscle: 'Calves',    equipment: 'Machine'    },
    { name: 'Donkey Calf Raise',           muscle: 'Calves',    equipment: 'Machine'    },
    { name: 'Leg Press Calf Raise',        muscle: 'Calves',    equipment: 'Machine'    },
    { name: 'Smith Machine Calf Raise',    muscle: 'Calves',    equipment: 'Machine'    },
    { name: 'Bodyweight Calf Raise',       muscle: 'Calves',    equipment: 'Bodyweight' },
    { name: 'Single-Leg Calf Raise',       muscle: 'Calves',    equipment: 'Bodyweight' },
    { name: 'Tibialis Raise',              muscle: 'Calves',    equipment: 'Bodyweight' },

    // ── FOREARMS ────────────────────────────────────────────────────
    { name: 'Wrist Curl',                  muscle: 'Forearms',  equipment: 'Barbell'    },
    { name: 'Reverse Wrist Curl',          muscle: 'Forearms',  equipment: 'Barbell'    },
    { name: 'Farmer\'s Walk',               muscle: 'Forearms',  equipment: 'Dumbbell'   },
    { name: 'Dead Hang',                   muscle: 'Forearms',  equipment: 'Bodyweight' },
    { name: 'Plate Pinch',                 muscle: 'Forearms',  equipment: 'Other'      },
    { name: 'Towel Pull-up',               muscle: 'Forearms',  equipment: 'Bodyweight' },
    { name: 'Reverse Curl',                muscle: 'Forearms',  equipment: 'Barbell'    },
    { name: 'Gripper',                     muscle: 'Forearms',  equipment: 'Other'      },
  ];

  // ── Storage ──────────────────────────────────────────────────────
  function loadStore() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return defaultStore();
      const parsed = JSON.parse(raw);
      // C6 fix: deep-merge `settings` so a stored `settings: {}` (or a
      // settings object missing newer fields) doesn't wipe out the default
      // `units: 'metric'`. Top-level fields stay shallow-merged.
      const base = defaultStore();
      const merged = Object.assign(base, parsed);
      merged.settings = Object.assign({}, base.settings, (parsed && parsed.settings) || {});
      return merged;
    } catch (_) {
      return defaultStore();
    }
  }
  function defaultStore() {
    return {
      sessions: [],
      bodyWeight: [],
      customExercises: [],
      routines: [],
      settings: { units: 'metric' },
    };
  }
  function saveStore(store) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
      window.dispatchEvent(new CustomEvent('workout-data:change'));
    } catch (e) { console.warn('WorkoutData save failed', e); }
  }

  // ── ID generation (no crypto.randomUUID dependency for older browsers) ──
  function uid() {
    return 'w_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  }

  // ── Public API ───────────────────────────────────────────────────
  const api = {
    EXERCISES,

    // Custom exercises (user-added) ──
    getCustomExercises() {
      return (loadStore().customExercises || []).slice();
    },
    /**
     * Add a custom exercise. Returns the created exercise (with `custom: true`).
     * Silently de-duplicates by lowercased name.
     */
    addCustomExercise(ex) {
      if (!ex || !ex.name) return null;
      const store = loadStore();
      store.customExercises = store.customExercises || [];
      const key = ex.name.trim().toLowerCase();
      if (!key) return null;
      // Reject duplicates (vs both built-in and existing custom)
      const dupeBuiltin = EXERCISES.some((e) => e.name.toLowerCase() === key);
      const dupeCustom  = store.customExercises.some((e) => e.name.toLowerCase() === key);
      if (dupeBuiltin || dupeCustom) return null;
      const created = {
        name: ex.name.trim(),
        muscle: ex.muscle || 'Other',
        equipment: ex.equipment || 'Other',
        custom: true,
      };
      store.customExercises.push(created);
      saveStore(store);
      return created;
    },
    deleteCustomExercise(name) {
      const store = loadStore();
      store.customExercises = (store.customExercises || []).filter(
        (e) => e.name.toLowerCase() !== String(name).toLowerCase()
      );
      saveStore(store);
    },

    // Routines (saved workout templates) ──
    /**
     * Returns saved routines, most-recently-used first (then most-recently-created).
     * Routine shape: { id, name, createdAt, lastUsedAt, exercises: [{ name, muscle, equipment, defaultSets }] }
     */
    getRoutines() {
      const list = (loadStore().routines || []).slice();
      list.sort((a, b) => (b.lastUsedAt || b.createdAt || 0) - (a.lastUsedAt || a.createdAt || 0));
      return list;
    },
    /**
     * Save a new routine. Returns the created routine (with id), or null if invalid.
     * Silently rejects empty names. De-dupes by case-insensitive name.
     */
    saveRoutine(routine) {
      if (!routine || !routine.name || !Array.isArray(routine.exercises) || !routine.exercises.length) return null;
      const store = loadStore();
      store.routines = store.routines || [];
      const key = routine.name.trim().toLowerCase();
      if (!key) return null;
      // If a routine with the same name already exists, REPLACE it (overwrite-on-save).
      const existingIdx = store.routines.findIndex((r) => (r.name || '').trim().toLowerCase() === key);
      const created = {
        id: existingIdx >= 0 ? store.routines[existingIdx].id : ('r_' + uid()),
        name: routine.name.trim(),
        createdAt: existingIdx >= 0 ? (store.routines[existingIdx].createdAt || Date.now()) : Date.now(),
        lastUsedAt: existingIdx >= 0 ? (store.routines[existingIdx].lastUsedAt || 0) : 0,
        exercises: routine.exercises.map((ex) => ({
          name: String(ex.name || '').trim(),
          muscle: ex.muscle || 'Other',
          equipment: ex.equipment || 'Other',
          defaultSets: Math.max(1, Math.min(20, parseInt(ex.defaultSets, 10) || 3)),
        })).filter((ex) => ex.name),
      };
      if (!created.exercises.length) return null;
      if (existingIdx >= 0) store.routines[existingIdx] = created;
      else store.routines.push(created);
      saveStore(store);
      return created;
    },
    /** Mark a routine as just-used (bumps lastUsedAt for sort priority). */
    markRoutineUsed(id) {
      const store = loadStore();
      const r = (store.routines || []).find((x) => x.id === id);
      if (!r) return;
      r.lastUsedAt = Date.now();
      saveStore(store);
    },
    /** Delete a routine by id. */
    deleteRoutine(id) {
      const store = loadStore();
      store.routines = (store.routines || []).filter((r) => r.id !== id);
      saveStore(store);
    },
    /**
     * All exercises (built-in + custom) — single source of truth for the picker.
     */
    allExercises() {
      return EXERCISES.concat(api.getCustomExercises());
    },

    // Sessions ─────────────────
    getSessions() {
      return loadStore().sessions.slice().sort((a, b) =>
        new Date(b.date) - new Date(a.date)
      );
    },
    saveSession(session) {
      const store = loadStore();
      const existing = store.sessions.findIndex((s) => s.id === session.id);
      if (existing >= 0) store.sessions[existing] = session;
      else store.sessions.push(Object.assign({ id: uid() }, session));
      saveStore(store);
      return session;
    },
    deleteSession(id) {
      const store = loadStore();
      store.sessions = store.sessions.filter((s) => s.id !== id);
      saveStore(store);
    },

    // Body weight ──────────────
    getBodyWeight() {
      return loadStore().bodyWeight.slice().sort((a, b) =>
        new Date(a.date) - new Date(b.date)
      );
    },
    addBodyWeight(entry) {
      const store = loadStore();
      // Replace same-day entry if it exists
      store.bodyWeight = store.bodyWeight.filter((e) => e.date !== entry.date);
      store.bodyWeight.push(entry);
      saveStore(store);
    },
    deleteBodyWeight(date) {
      const store = loadStore();
      store.bodyWeight = store.bodyWeight.filter((e) => e.date !== date);
      saveStore(store);
    },

    // Settings ─────────────────
    getSettings() { return loadStore().settings; },
    setUnits(units) {
      const store = loadStore();
      store.settings.units = units;
      saveStore(store);
    },

    /**
     * Checks if an exercise or exercise name represents a cardio exercise.
     */
    isCardioExercise(exOrName) {
      if (!exOrName) return false;
      if (typeof exOrName === 'object') {
        if (String(exOrName.muscle || '').toLowerCase() === 'cardio') return true;
        return api.isCardioExercise(exOrName.name);
      }
      const name = String(exOrName).toLowerCase().trim();
      const all = api.allExercises();
      const match = all.find((e) => e.name.toLowerCase() === name);
      if (match && String(match.muscle || '').toLowerCase() === 'cardio') return true;
      const CARDIO_NAMES = [
        'treadmill run', 'treadmill walk (incline)', 'treadmill walk', 'treadmill',
        'stationary bike', 'exercise bike', 'spin bike', 'cycling', 'bike',
        'rowing machine', 'rower',
        'stair master', 'stair climber', 'stairs',
        'elliptical', 'cross trainer',
        'jump rope', 'skipping',
        'battle ropes',
        'sled push', 'sled pull',
        'burpee', 'burpees',
        'box jump', 'box jumps',
        'assault bike', 'air bike', 'echo bike',
        'ski erg', 'running', 'jogging', 'walking', 'sprints'
      ];
      return CARDIO_NAMES.some((c) => name === c || name.includes(c));
    },

    // Analytics helpers ────────
    /**
     * Find the most recent set logged for an exercise (any session).
     * Returns the heaviest top set across the session for quick reference.
     */
    getLastPerformance(exerciseName) {
      const sessions = api.getSessions();
      const isCardio = api.isCardioExercise(exerciseName);
      for (let i = 0; i < sessions.length; i++) {
        const s = sessions[i];
        const ex = s.exercises.find((e) => e.name === exerciseName);
        if (ex && ex.sets && ex.sets.length) {
          // Exclude warmup sets — they're not representative of working performance.
          const working = ex.sets.filter((set) => set.type !== 'warmup');
          const completed = working.filter((set) => set.completed && (
            isCardio
              ? ((set.time || set.reps || 0) > 0 || (set.distance || set.weight || 0) > 0)
              : (set.weight > 0 || set.reps > 0)
          ));
          const setsToUse = completed.length ? completed : (working.length ? working : ex.sets);
          const top = isCardio
            ? setsToUse.reduce((best, set) => {
                const dBest = (best.distance != null ? best.distance : best.weight) || 0;
                const dCur  = (set.distance != null ? set.distance : set.weight) || 0;
                const tBest = (best.time != null ? best.time : best.reps) || 0;
                const tCur  = (set.time != null ? set.time : set.reps) || 0;
                return (dCur * 1000 + tCur) > (dBest * 1000 + tBest) ? set : best;
              }, setsToUse[0])
            : setsToUse.reduce((best, set) =>
                (set.weight || 0) > (best.weight || 0) ? set : best, setsToUse[0]);
          return {
            date: s.date,
            time: top.time != null ? top.time : top.reps,
            distance: top.distance != null ? top.distance : top.weight,
            distanceUnit: top.distanceUnit || (s.unit === 'lb' ? 'mi' : 'km'),
            weight: top.weight,
            reps: top.reps,
            unit: top.unit || s.unit
          };
        }
      }
      return null;
    },

    /**
     * Total volume of a session = sum(weight × reps) across all completed
     * working sets. Warmup sets and cardio exercises are excluded.
     */
    sessionVolume(session) {
      let total = 0;
      session.exercises.forEach((ex) => {
        if (api.isCardioExercise(ex)) return;
        ex.sets.forEach((set) => {
          if (!set.completed || set.type === 'warmup') return;
          total += (set.weight || 0) * (set.reps || 0);
        });
      });
      return Math.round(total);
    },

    /**
     * Estimated 1-rep max via Epley formula.
     */
    estimated1RM(weight, reps) {
      if (!weight || !reps) return 0;
      if (reps === 1) return weight;
      return Math.round(weight * (1 + reps / 30));
    },

    /**
     * Per-exercise progression series. Returns chronological [{date, value, unit}].
     */
    exerciseProgression(exerciseName) {
      const isCardio = api.isCardioExercise(exerciseName);
      const sessions = api.getSessions().slice().reverse(); // oldest → newest
      const points = [];
      sessions.forEach((s) => {
        const ex = s.exercises.find((e) => e.name === exerciseName);
        if (!ex || !ex.sets || !ex.sets.length) return;
        if (isCardio) {
          let bestVal = 0;
          let bestUnit = 'min';
          ex.sets.forEach((set) => {
            if (set.type === 'warmup') return;
            const t = set.time != null ? set.time : (set.reps || 0);
            const d = set.distance != null ? set.distance : (set.weight || 0);
            const du = set.distanceUnit || (s.unit === 'lb' ? 'mi' : 'km');
            if (d > 0) {
              if (d > bestVal) { bestVal = d; bestUnit = du; }
            } else if (t > 0 && bestVal === 0) {
              bestVal = t; bestUnit = 'min';
            }
          });
          if (bestVal > 0) points.push({ date: s.date, value: bestVal, unit: bestUnit });
        } else {
          const top = ex.sets.reduce((best, set) => {
            if (set.type === 'warmup') return best;
            const oneRm = api.estimated1RM(set.weight || 0, set.reps || 0);
            return oneRm > best ? oneRm : best;
          }, 0);
          if (top > 0) points.push({ date: s.date, value: top, unit: s.unit || 'kg' });
        }
      });
      return points;
    },

    /**
     * Per-exercise progression of the heaviest single weight lifted (or longest distance for cardio).
     * Returns chronological [{date, value, reps, unit}].
     */
    bestWeightProgression(exerciseName) {
      const isCardio = api.isCardioExercise(exerciseName);
      const sessions = api.getSessions().slice().reverse();
      const points = [];
      sessions.forEach((s) => {
        const ex = s.exercises.find((e) => e.name === exerciseName);
        if (!ex || !ex.sets || !ex.sets.length) return;
        if (isCardio) {
          let best = null;
          ex.sets.forEach((set) => {
            if (set.type === 'warmup') return;
            if (!set.completed) return;
            const t = set.time != null ? set.time : (set.reps || 0);
            const d = set.distance != null ? set.distance : (set.weight || 0);
            const du = set.distanceUnit || (s.unit === 'lb' ? 'mi' : 'km');
            if (d > 0 && (!best || d > best.value)) {
              best = { value: d, reps: t, unit: du };
            } else if (t > 0 && !best) {
              best = { value: t, reps: t, unit: 'min' };
            }
          });
          if (best) points.push({ date: s.date, value: best.value, reps: best.reps, unit: best.unit });
        } else {
          let best = null;
          ex.sets.forEach((set) => {
            if (set.type === 'warmup') return;
            if (!set.completed || !set.weight) return;
            if (!best || set.weight > best.weight) {
              best = { weight: set.weight, reps: set.reps || 0, unit: set.unit || s.unit || 'kg' };
            }
          });
          if (best) points.push({ date: s.date, value: best.weight, reps: best.reps, unit: best.unit });
        }
      });
      return points;
    },

    /**
     * Per-exercise total reps (or duration in minutes for cardio) per session.
     * Returns chronological [{date, value: totalReps}]. Warmup sets excluded.
     */
    totalRepsProgression(exerciseName) {
      const isCardio = api.isCardioExercise(exerciseName);
      const sessions = api.getSessions().slice().reverse();
      const points = [];
      sessions.forEach((s) => {
        const ex = s.exercises.find((e) => e.name === exerciseName);
        if (!ex || !ex.sets || !ex.sets.length) return;
        let total = 0;
        ex.sets.forEach((set) => {
          if (set.type === 'warmup') return;
          if (!set.completed) return;
          if (isCardio) {
            total += (set.time != null ? set.time : (set.reps || 0));
          } else {
            total += set.reps || 0;
          }
        });
        if (total > 0) points.push({ date: s.date, value: total, unit: isCardio ? 'min' : 'reps' });
      });
      return points;
    },

    /**
     * Per-exercise total volume per session (sum of weight × reps, or distance for cardio).
     * Returns chronological [{date, value: totalVolume, unit}].
     */
    volumeProgression(exerciseName) {
      const isCardio = api.isCardioExercise(exerciseName);
      const sessions = api.getSessions().slice().reverse();
      const points = [];
      sessions.forEach((s) => {
        const ex = s.exercises.find((e) => e.name === exerciseName);
        if (!ex || !ex.sets || !ex.sets.length) return;
        let total = 0;
        let distUnit = s.unit === 'lb' ? 'mi' : 'km';
        ex.sets.forEach((set) => {
          if (set.type === 'warmup') return;
          if (!set.completed) return;
          if (isCardio) {
            const d = set.distance != null ? set.distance : (set.weight || 0);
            const t = set.time != null ? set.time : (set.reps || 0);
            total += d > 0 ? d : t;
            if (d > 0) distUnit = set.distanceUnit || distUnit;
            else distUnit = 'min';
          } else {
            total += (set.weight || 0) * (set.reps || 0);
          }
        });
        if (total > 0) points.push({ date: s.date, value: isCardio ? +total.toFixed(2) : Math.round(total), unit: isCardio ? distUnit : (s.unit || 'kg') });
      });
      return points;
    },

    /**
     * All exercise names that have ever been logged (alphabetical).
     */
    loggedExerciseNames() {
      const set = new Set();
      api.getSessions().forEach((s) =>
        s.exercises.forEach((e) => set.add(e.name))
      );
      return Array.from(set).sort();
    },

    /**
     * Weekly volume series for the last N weeks.
     */
    weeklyVolume(weeksBack) {
      const now = new Date();
      const buckets = [];
      for (let i = weeksBack - 1; i >= 0; i--) {
        const start = new Date(now);
        start.setDate(now.getDate() - (i * 7 + 6));
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(start.getDate() + 7);
        buckets.push({ start, end, volume: 0, count: 0 });
      }
      api.getSessions().forEach((s) => {
        const d = new Date(s.date);
        const bucket = buckets.find((b) => d >= b.start && d < b.end);
        if (bucket) {
          bucket.volume += api.sessionVolume(s);
          bucket.count += 1;
        }
      });
      return buckets;
    },

    // Export / import ──────────
    exportJSON() { return JSON.stringify(loadStore(), null, 2); },

    /**
     * Build standard, rich CSV of all workout sessions.
     */
    exportCSV() {
      const sessions = api.getSessions();
      const rows = [
        ['Date', 'Workout Name', 'Exercise', 'Muscle Group', 'Set', 'Type', 'Weight', 'Reps', 'Unit', 'Time (min)', 'Distance', 'Rest (sec)', 'Notes', 'Completed']
      ];
      sessions.forEach((s) => {
        const dateStr = s.date ? new Date(s.date).toISOString().slice(0, 10) : '';
        const workoutName = s.name || 'Workout';
        const distUnit = s.unit === 'lb' ? 'mi' : 'km';
        (s.exercises || []).forEach((ex) => {
          const isCardio = api.isCardioExercise(ex);
          (ex.sets || []).forEach((set, i) => {
            const setType = (set.type || 'normal').charAt(0).toUpperCase() + (set.type || 'normal').slice(1);
            if (isCardio) {
              const t = set.time != null ? set.time : (set.reps || 0);
              const d = set.distance != null ? set.distance : (set.weight || 0);
              rows.push([
                dateStr, workoutName, ex.name, ex.muscle || 'Cardio', i + 1, setType,
                0, 0, s.unit || 'kg',
                t > 0 ? t : '', d > 0 ? d : '',
                Math.max(0, Math.round(set.restSeconds || 0)),
                set.notes || '',
                set.completed ? 'Y' : 'N'
              ]);
            } else {
              rows.push([
                dateStr, workoutName, ex.name, ex.muscle || 'Other', i + 1, setType,
                set.weight != null ? set.weight : 0,
                set.reps != null ? set.reps : 0,
                set.unit || s.unit || 'kg',
                '', '',
                Math.max(0, Math.round(set.restSeconds || 0)),
                set.notes || '',
                set.completed ? 'Y' : 'N'
              ]);
            }
          });
        });
      });

      // Bodyweight entries if present
      api.getBodyWeight().forEach((b) => {
        rows.push([b.date, 'Bodyweight Log', 'BODYWEIGHT', 'Bodyweight', 1, 'Normal', b.weight, 1, b.unit || 'kg', '', '', 0, '', 'Y']);
      });

      const esc = (v) => {
        if (v == null) return '';
        const s = String(v);
        return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
      };
      return rows.map((r) => r.map(esc).join(',')).join('\r\n');
    },

    /**
     * Generates a rich, well-documented demo CSV template.
     * Ready for users, coaches, and AI prompts (ChatGPT, Claude, etc.).
     */
    getDemoCSV() {
      return [
        '# ==============================================================================',
        '# AMINOBAE FITNESS OS — WORKOUT IMPORT / EXPORT TEMPLATE',
        '# ==============================================================================',
        '# INSTRUCTIONS FOR PERSONAL TRAINERS & AI (ChatGPT, Claude, DeepSeek):',
        '# 1. Fill in your workout sessions below using these exact column headers.',
        '# 2. You can plan past, current, or multi-day training splits.',
        '# 3. Accepted Values:',
        '#    - Date: YYYY-MM-DD (e.g. 2026-08-21)',
        '#    - Workout Name: Name of the session (e.g. Push Day A, Pull Day, Leg Hypertrophy)',
        '#    - Exercise: Name of movement (e.g. Barbell Bench Press, Incline DB Press, Treadmill Run)',
        '#    - Muscle Group: Chest, Back, Legs, Shoulders, Arms, Core, Glutes, Calves, Cardio, Other',
        '#    - Set: 1, 2, 3, 4, ...',
        '#    - Type: Normal, Warmup, Drop, Failure',
        '#    - Weight: Numeric load in kg/lb (e.g. 80, 22.5, 0 for bodyweight/cardio)',
        '#    - Reps: Numeric repetition count (e.g. 10, 8, 12, 0 for cardio)',
        '#    - Unit: kg or lb',
        '#    - Time (min): Duration in minutes for cardio / holds (e.g. 25, 15, 0)',
        '#    - Distance: Numeric distance in km/mi for cardio (e.g. 4.2, 3.0, 0)',
        '#    - Rest (sec): Target rest timer in seconds (e.g. 90, 120, 60)',
        '#    - Notes: Optional coaching cues or RPE notes (e.g. 2s pause at bottom, RPE 8)',
        '#    - Completed: Y or N (Y = logged workout, N = planned/uncompleted)',
        '# ==============================================================================',
        'Date,Workout Name,Exercise,Muscle Group,Set,Type,Weight,Reps,Unit,Time (min),Distance,Rest (sec),Notes,Completed',
        '2026-08-18,Push Day A,Barbell Bench Press,Chest,1,Warmup,40,15,kg,0,0,60,Warm-up set,Y',
        '2026-08-18,Push Day A,Barbell Bench Press,Chest,2,Normal,80,10,kg,0,0,120,Working set 1,Y',
        '2026-08-18,Push Day A,Barbell Bench Press,Chest,3,Normal,85,8,kg,0,0,120,Working set 2,Y',
        '2026-08-18,Push Day A,Barbell Bench Press,Chest,4,Failure,90,6,kg,0,0,180,Push to failure,Y',
        '2026-08-18,Push Day A,Incline Dumbbell Press,Chest,1,Normal,28,10,kg,0,0,90,30 deg incline,Y',
        '2026-08-18,Push Day A,Incline Dumbbell Press,Chest,2,Normal,30,8,kg,0,0,90,,Y',
        '2026-08-18,Push Day A,Incline Dumbbell Press,Chest,3,Drop,32,6,kg,0,0,90,Drop to 20kg after,Y',
        '2026-08-18,Push Day A,Lateral Raise,Shoulders,1,Normal,12,15,kg,0,0,60,Control eccentric,Y',
        '2026-08-18,Push Day A,Lateral Raise,Shoulders,2,Normal,12,12,kg,0,0,60,,Y',
        '2026-08-18,Push Day A,Lateral Raise,Shoulders,3,Normal,14,10,kg,0,0,60,,Y',
        '2026-08-18,Push Day A,Triceps Rope Pushdown,Arms,1,Normal,25,12,kg,0,0,60,Squeeze at bottom,Y',
        '2026-08-18,Push Day A,Triceps Rope Pushdown,Arms,2,Normal,27.5,10,kg,0,0,60,,Y',
        '2026-08-18,Push Day A,Triceps Rope Pushdown,Arms,3,Failure,30,8,kg,0,0,90,To failure,Y',
        '2026-08-19,Pull Day,Deadlift,Back,1,Warmup,60,10,kg,0,0,90,Warm-up,Y',
        '2026-08-19,Pull Day,Deadlift,Back,2,Normal,120,6,kg,0,0,180,Clean form,Y',
        '2026-08-19,Pull Day,Deadlift,Back,3,Normal,130,5,kg,0,0,180,,Y',
        '2026-08-19,Pull Day,Lat Pulldown,Back,1,Normal,60,10,kg,0,0,90,Wide grip,Y',
        '2026-08-19,Pull Day,Lat Pulldown,Back,2,Normal,65,8,kg,0,0,90,,Y',
        '2026-08-19,Pull Day,Dumbbell Row,Back,1,Normal,32,10,kg,0,0,90,Each arm,Y',
        '2026-08-19,Pull Day,Dumbbell Row,Back,2,Normal,34,8,kg,0,0,90,,Y',
        '2026-08-19,Pull Day,Barbell Curl,Arms,1,Normal,30,10,kg,0,0,60,Strict form,Y',
        '2026-08-19,Pull Day,Barbell Curl,Arms,2,Normal,32.5,8,kg,0,0,60,,Y',
        '2026-08-20,Leg Day & Core,Back Squat,Legs,1,Warmup,50,12,kg,0,0,90,Warm-up,Y',
        '2026-08-20,Leg Day & Core,Back Squat,Legs,2,Normal,100,8,kg,0,0,180,Parallel depth,Y',
        '2026-08-20,Leg Day & Core,Back Squat,Legs,3,Normal,110,6,kg,0,0,180,,Y',
        '2026-08-20,Leg Day & Core,Romanian Deadlift,Legs,1,Normal,80,10,kg,0,0,90,Feel hamstring stretch,Y',
        '2026-08-20,Leg Day & Core,Romanian Deadlift,Legs,2,Normal,90,8,kg,0,0,90,,Y',
        '2026-08-20,Leg Day & Core,Leg Extension,Legs,1,Normal,45,12,kg,0,0,60,,Y',
        '2026-08-20,Leg Day & Core,Leg Extension,Legs,2,Normal,50,10,kg,0,0,60,,Y',
        '2026-08-20,Leg Day & Core,Plank,Core,1,Normal,0,0,kg,1.5,0,60,90 sec hold,Y',
        '2026-08-20,Leg Day & Core,Plank,Core,2,Normal,0,0,kg,1.5,0,60,90 sec hold,Y',
        '2026-08-21,Cardio & HIIT,Treadmill Run,Cardio,1,Normal,0,0,kg,25,4.2,60,Moderate steady pace,Y',
        '2026-08-21,Cardio & HIIT,Rowing Machine,Cardio,1,Normal,0,0,kg,15,3.0,60,500m intervals,Y'
      ].join('\r\n');
    },

    /**
     * Robust CSV Parser & Importer.
     * Reads workout CSV, creates sessions, registers custom exercises, and saves routines.
     * Returns: { success: boolean, sessionsCount: number, exercisesCount: number, setsCount: number, routinesCount: number, error?: string }
     */
    importCSV(csvText, options = {}) {
      try {
        if (!csvText || typeof csvText !== 'string') throw new Error('Empty or invalid CSV file');

        // Split into lines & filter out comment / empty lines
        const rawLines = csvText.split(/\r?\n/);
        const dataLines = [];
        for (let i = 0; i < rawLines.length; i++) {
          const trimmed = rawLines[i].trim();
          if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith('//')) continue;
          dataLines.push(rawLines[i]);
        }

        if (dataLines.length < 2) throw new Error('CSV must contain a header row and at least one data row');

        // Helper to parse a single CSV line with quotes and commas
        function parseLine(line, delimiter) {
          const result = [];
          let current = '';
          let inQuotes = false;
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const next = line[i + 1];
            if (char === '"') {
              if (inQuotes && next === '"') {
                current += '"';
                i++; // skip escaped quote
              } else {
                inQuotes = !inQuotes;
              }
            } else if (char === delimiter && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          result.push(current.trim());
          return result;
        }

        // Auto-detect delimiter from header line
        const headerLine = dataLines[0];
        let delimiter = ',';
        if (headerLine.includes(';') && (headerLine.split(';').length > headerLine.split(',').length)) {
          delimiter = ';';
        } else if (headerLine.includes('\t') && (headerLine.split('\t').length > headerLine.split(',').length)) {
          delimiter = '\t';
        }

        const rawHeaders = parseLine(headerLine, delimiter);
        const colMap = {};
        rawHeaders.forEach((h, idx) => {
          const norm = h.toLowerCase().replace(/[^a-z0-9]/g, '');
          if (norm.includes('date') || norm === 'day' || norm === 'time') colMap.date = idx;
          if (norm.includes('workout') || norm.includes('routine') || norm.includes('session') || norm === 'name') colMap.workoutName = idx;
          if (norm.includes('exercise') || norm === 'movement') colMap.exercise = idx;
          if (norm.includes('muscle') || norm.includes('category') || norm === 'target') colMap.muscle = idx;
          if (norm === 'set' || norm.includes('setnum') || norm === 'setno' || norm === 'set') colMap.set = idx;
          if (norm === 'type' || norm.includes('settype')) colMap.type = idx;
          if (norm === 'weight' || norm === 'load' || norm === 'kg' || norm === 'lbs' || norm === 'weightkg' || norm === 'weightlb') colMap.weight = idx;
          if (norm === 'reps' || norm === 'repetitions' || norm === 'count') colMap.reps = idx;
          if (norm === 'unit' || norm === 'units' || norm === 'weightunit') colMap.unit = idx;
          if (norm.includes('timemin') || norm === 'time' || norm === 'duration' || norm === 'minutes' || norm === 'min') colMap.time = idx;
          if (norm.includes('distance') || norm === 'dist' || norm === 'km' || norm === 'miles' || norm === 'mi') colMap.distance = idx;
          if (norm.includes('rest') || norm.includes('restsec')) colMap.rest = idx;
          if (norm.includes('note') || norm.includes('comment')) colMap.notes = idx;
          if (norm.includes('completed') || norm === 'done' || norm === 'status') colMap.completed = idx;
        });

        // Fallback: if 'exercise' wasn't mapped by keyword, check for first non-date text column
        if (colMap.exercise == null) {
          rawHeaders.forEach((h, idx) => {
            if (idx !== colMap.date && idx !== colMap.workoutName && colMap.exercise == null) colMap.exercise = idx;
          });
        }

        if (colMap.exercise == null) throw new Error('Could not find an "Exercise" column in CSV headers');

        const defaultUnit = api.getSettings().units === 'imperial' ? 'lb' : 'kg';
        const todayStr = new Date().toISOString().slice(0, 10);

        // Group rows into Sessions by (Date + WorkoutName)
        const sessionGroups = new Map();
        let totalSetsCount = 0;
        let totalExercisesCount = 0;

        for (let i = 1; i < dataLines.length; i++) {
          const cols = parseLine(dataLines[i], delimiter);
          if (!cols || cols.length === 0 || (cols.length === 1 && !cols[0])) continue;

          const rawEx = colMap.exercise != null ? cols[colMap.exercise] : '';
          if (!rawEx || !rawEx.trim()) continue;
          const exName = rawEx.trim();

          // Bodyweight row detection
          if (exName.toUpperCase() === 'BODYWEIGHT') {
            const bwDate = (colMap.date != null && cols[colMap.date]) ? cols[colMap.date].trim() : todayStr;
            const bwVal = parseFloat(colMap.weight != null ? cols[colMap.weight] : cols[1]) || 0;
            const bwUnit = (colMap.unit != null && cols[colMap.unit]) ? cols[colMap.unit].trim() : defaultUnit;
            if (bwVal > 0) {
              api.addBodyWeight({ date: bwDate, weight: bwVal, unit: bwUnit });
            }
            continue;
          }

          let dateVal = (colMap.date != null && cols[colMap.date]) ? cols[colMap.date].trim() : todayStr;
          // Normalise date format (supports YYYY-MM-DD, DD/MM/YYYY, MM/DD/YYYY)
          if (/^\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4}$/.test(dateVal)) {
            const parts = dateVal.split(/[\/\-\.]/);
            // Assume YYYY at end or start
            if (parts[2].length === 4) {
              const y = parts[2];
              const m = parts[1].padStart(2, '0');
              const d = parts[0].padStart(2, '0');
              dateVal = `${y}-${m}-${d}`;
            }
          }
          if (!/^\d{4}-\d{2}-\d{2}$/.test(dateVal)) {
            dateVal = todayStr;
          }

          const workoutName = (colMap.workoutName != null && cols[colMap.workoutName]) ? cols[colMap.workoutName].trim() : 'Workout';
          const sessionKey = `${dateVal}___${workoutName.toLowerCase()}`;

          if (!sessionGroups.has(sessionKey)) {
            sessionGroups.set(sessionKey, {
              date: dateVal,
              name: workoutName,
              unit: defaultUnit,
              exercisesMap: new Map(),
            });
          }

          const sessionObj = sessionGroups.get(sessionKey);

          // Find or create Exercise in this session
          const exKey = exName.toLowerCase();
          if (!sessionObj.exercisesMap.has(exKey)) {
            // Determine muscle group
            let muscle = (colMap.muscle != null && cols[colMap.muscle]) ? cols[colMap.muscle].trim() : '';
            if (!muscle) {
              const matched = api.allExercises().find((e) => e.name.toLowerCase() === exKey);
              muscle = matched ? matched.muscle : 'Other';
            }

            // Auto-register as custom exercise if not in database
            const isKnown = api.allExercises().some((e) => e.name.toLowerCase() === exKey);
            if (!isKnown) {
              api.addCustomExercise({ name: exName, muscle: muscle || 'Other', equipment: 'Other' });
            }

            sessionObj.exercisesMap.set(exKey, {
              name: exName,
              muscle: muscle || 'Other',
              equipment: 'Other',
              sets: [],
            });
            totalExercisesCount++;
          }

          const exObj = sessionObj.exercisesMap.get(exKey);
          const isCardio = api.isCardioExercise({ name: exName, muscle: exObj.muscle });

          // Parse Set details
          let rawType = (colMap.type != null && cols[colMap.type]) ? cols[colMap.type].toLowerCase().trim() : 'normal';
          if (!['normal', 'warmup', 'drop', 'failure'].includes(rawType)) rawType = 'normal';

          const weightVal = parseFloat(colMap.weight != null ? cols[colMap.weight] : '0') || 0;
          const repsVal   = parseInt(colMap.reps != null ? cols[colMap.reps] : '0', 10) || 0;
          const timeVal   = parseFloat(colMap.time != null ? cols[colMap.time] : '0') || 0;
          const distVal   = parseFloat(colMap.distance != null ? cols[colMap.distance] : '0') || 0;
          const restVal   = parseInt(colMap.rest != null ? cols[colMap.rest] : '0', 10) || 0;
          const notesVal  = (colMap.notes != null && cols[colMap.notes]) ? cols[colMap.notes].trim() : '';
          const unitVal   = (colMap.unit != null && cols[colMap.unit]) ? cols[colMap.unit].toLowerCase().trim() : defaultUnit;

          let isCompleted = true;
          if (colMap.completed != null && cols[colMap.completed]) {
            const compStr = cols[colMap.completed].toLowerCase().trim();
            if (['n', 'no', 'false', '0', 'uncompleted', 'pending'].includes(compStr)) {
              isCompleted = false;
            }
          }

          if (isCardio) {
            exObj.sets.push({
              type: rawType,
              time: timeVal > 0 ? timeVal : (repsVal > 0 ? repsVal : undefined),
              distance: distVal > 0 ? distVal : (weightVal > 0 ? weightVal : undefined),
              distanceUnit: unitVal === 'lb' ? 'mi' : 'km',
              restSeconds: restVal,
              notes: notesVal,
              completed: isCompleted,
            });
          } else {
            exObj.sets.push({
              type: rawType,
              weight: weightVal,
              reps: repsVal,
              unit: unitVal,
              restSeconds: restVal,
              notes: notesVal,
              completed: isCompleted,
            });
          }

          totalSetsCount++;
        }

        if (sessionGroups.size === 0) throw new Error('No valid workout rows found in CSV');

        const store = loadStore();
        store.sessions = store.sessions || [];
        store.routines = store.routines || [];

        let routinesCount = 0;
        let sessionsCount = 0;

        sessionGroups.forEach((sGroup) => {
          const exerciseList = Array.from(sGroup.exercisesMap.values());
          if (!exerciseList.length) return;

          const sessionPayload = {
            id: uid(),
            date: new Date(sGroup.date + 'T12:00:00').toISOString(),
            name: sGroup.name || undefined,
            unit: sGroup.unit || defaultUnit,
            exercises: exerciseList,
          };

          // Append or merge to sessions
          store.sessions.push(sessionPayload);
          sessionsCount++;

          // Auto-save distinct routine templates if requested
          if (options.createRoutines !== false && sGroup.name && sGroup.name.toLowerCase() !== 'workout') {
            const rName = sGroup.name.trim();
            const existingR = store.routines.find((r) => r.name.toLowerCase() === rName.toLowerCase());
            const routineData = {
              id: existingR ? existingR.id : ('r_' + uid()),
              name: rName,
              createdAt: existingR ? existingR.createdAt : Date.now(),
              lastUsedAt: Date.now(),
              exercises: exerciseList.map((ex) => ({
                name: ex.name,
                muscle: ex.muscle,
                equipment: ex.equipment || 'Other',
                defaultSets: Math.max(1, ex.sets.length || 3),
              })),
            };
            if (existingR) {
              const idx = store.routines.indexOf(existingR);
              store.routines[idx] = routineData;
            } else {
              store.routines.push(routineData);
              routinesCount++;
            }
          }
        });

        // Re-sort sessions chronologically
        store.sessions.sort((a, b) => new Date(b.date) - new Date(a.date));
        saveStore(store);

        return {
          success: true,
          sessionsCount,
          exercisesCount: totalExercisesCount,
          setsCount: totalSetsCount,
          routinesCount,
        };
      } catch (err) {
        console.warn('WorkoutData CSV import error:', err);
        return {
          success: false,
          error: err.message || 'Failed to parse CSV file',
        };
      }
    },

    /**
     * Import a JSON backup. Validates shape so a malformed file can't
     * corrupt the store.
     */
    importJSON(jsonText) {
      try {
        const parsed = JSON.parse(jsonText);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          throw new Error('Backup must be a JSON object');
        }
        const validated = defaultStore();
        if (Array.isArray(parsed.sessions))        validated.sessions        = parsed.sessions;
        if (Array.isArray(parsed.bodyWeight))      validated.bodyWeight      = parsed.bodyWeight;
        if (Array.isArray(parsed.customExercises)) validated.customExercises = parsed.customExercises;
        if (Array.isArray(parsed.routines))        validated.routines        = parsed.routines;
        if (parsed.settings && typeof parsed.settings === 'object' && !Array.isArray(parsed.settings)) {
          validated.settings = {
            units: parsed.settings.units === 'imperial' ? 'imperial' : 'metric',
          };
        }
        saveStore(validated);
        return true;
      } catch (e) {
        console.warn('WorkoutData import failed', e);
        return false;
      }
    },
    clearAll() {
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
      window.dispatchEvent(new CustomEvent('workout-data:change'));
    },
  };

  window.WorkoutData = api;
})();
